import { toast } from '@gmg/gmg-react-components';
import { FunctionComponent, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import AppContext from 'src/AppContext';
import { useCreateTemporaryLicenses } from 'src/graphql/useCreateTemporaryLicenses';
import { organizationPaths } from 'src/routes';
import { getRequestTemporaryLicensesEvent } from 'src/tracking';
import RequestTemporaryLicensesModal from './RequestTemporaryLicensesModal';

interface RequestTemporaryLicensesModalContainerProps {
    temporaryLicensesSerialNumber: string;
    onClose?: () => void;
}

const RequestTemporaryLicensesModalContainer: FunctionComponent<RequestTemporaryLicensesModalContainerProps> = (props) => {
    const { t } = useTranslation();
    const { trackEvent } = useContext(AppContext);
    const { orgId: targetOrgId } = useParams();

    const navigate = useNavigate();
    const createTemporaryLicenses = useCreateTemporaryLicenses(targetOrgId!);

    const getErrorMsgForCreateTemporaryLicensesToast = (errorType: string): string => {

        switch (errorType) {
            case 'AssignedToDifferentOrg':
                return t('RequestTemporaryLicenses.toast_error_desc.assignedToDifferentOrg', 'The license system is owned by a different organization.');
            case 'Unauthorized':
                return t('RequestTemporaryLicenses.toast_error_desc.unauthorized', 'You are not permitted to request temporary licenses for this license system.');
            case 'InvalidInput':
                return t('RequestTemporaryLicenses.toast_error_desc.invalidLicenseSystemId', 'The license system ID is not valid.');
            default:
                return t('Common.Toast.Error', 'An error occurred');
        }
    };

    const handleSubmit = async () => {
        trackEvent(getRequestTemporaryLicensesEvent());
        void toast.promise(createTemporaryLicenses.mutateAsync(props.temporaryLicensesSerialNumber), {
            loading: t('Common.Toast.Loading', 'Loading'),
            success: (t('RequestTemporaryLicenses.toast_success_desc', {
                defaultValue: 'Request for system "{{serialNumber}}" successfully sent. Please wait a few minutes until the process is complete.',
                serialNumber: props.temporaryLicensesSerialNumber,
            })),
            error: (response) => getErrorMsgForCreateTemporaryLicensesToast(response.errors[0].errorType),
        });
    };

    return (
        <RequestTemporaryLicensesModal
            serialNumber={props.temporaryLicensesSerialNumber}
            onSubmit={handleSubmit}
            onCloseModal={() => {
                props.onClose?.();
                // Navigate resets the location state. If we don't do it, the modal dialog box will be shown on page refresh.
                navigate(organizationPaths.systemLicences(targetOrgId!));
            }}
        />
    );
};

export default RequestTemporaryLicensesModalContainer;
